import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { FileText, Download, Calendar, X, RefreshCw, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import './AttendanceReport.css';

const AttendanceReport = () => {
  const [reportType, setReportType] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(''); // Add status filter
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('summary'); // 'summary' or 'records'
  const [recordsData, setRecordsData] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 1024 : false
  );
  const itemsPerPage = 10;

  useEffect(() => {
    fetchDepartments();
    fetchRoles();
    fetchEmployees();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await adminAPI.getDepartments();
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await adminAPI.getRoles();
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await adminAPI.getEmployees();
      console.log('📋 Employees loaded:', response.data);
      
      // Map employees to use employee_id as the primary identifier
      const mappedEmployees = response.data.map(emp => {
        const employeeId = emp.employee_id || emp.employeeid;
        return {
          ...emp,
          employee_id: employeeId,
          display_id: employeeId
        };
      });
      
      console.log('📋 Mapped employees with employee_id:', mappedEmployees);
      setEmployees(mappedEmployees);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const handleShowReport = async () => {
    // Validate custom date range
    if (reportType === 'custom' && new Date(startDate) > new Date(endDate)) {
      toast.warning('Start date cannot be after end date');
      return;
    }

    setLoading(true);
    try {
      const params = {
        reportType,
        date: reportType === 'daily' ? selectedDate : undefined,
        month: reportType === 'monthly' ? selectedMonth : undefined,
        startDate: reportType === 'custom' ? startDate : undefined,
        endDate: reportType === 'custom' ? endDate : undefined,
        departmentId: selectedDepartment || undefined,
        roleId: selectedRole || undefined,
        employeeId: selectedEmployee || undefined,
      };

      console.log('📊 Fetching attendance report with params:', params);

      const response = await adminAPI.getAttendanceReport(params);
      console.log('📥 Backend API Response:', response.data);

      // If "All Employees" is selected
      if (!selectedEmployee || selectedEmployee === '') {
        if (response.data && response.data.records) {
          setReportData({
            records: response.data.records,
            count: response.data.records.length,
            employeeName: 'All Employees',
            enrollId: 'ALL',
          });
          console.log(`Found ${response.data.records.length} attendance records`);
        } else if (response.data && response.data.message) {
          console.log(response.data.message);
          setReportData({ records: [], count: 0, employeeName: 'All Employees', enrollId: 'ALL' });
        } else {
          toast.error('Failed to fetch attendance report');
        }
      } else {
        // Single employee report
        const employee = employees.find(emp => emp.employee_id === selectedEmployee);
        
        console.log('👤 Selected employee:', employee);
        console.log('🔍 Selected employee_id:', selectedEmployee);
        
        if (!employee) {
          toast.error('Employee not found');
          setLoading(false);
          return;
        }

        const employeeName = employee.person_name || employee.name || 'Unknown Employee';

        if (response.data && response.data.records) {
          setReportData({
            records: response.data.records,
            count: response.data.records.length,
            employeeName: employeeName,
            enrollId: employee.employee_id,
          });
          console.log(`Found ${response.data.records.length} attendance records for ${employeeName}`);
        } else if (response.data && response.data.message) {
          console.log(response.data.message);
          setReportData({ records: [], count: 0, employeeName: employeeName, enrollId: employee.employee_id });
        } else {
          toast.error('Failed to fetch attendance report');
        }
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Failed to fetch report from biometric system');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    console.log('🔍 PDF Download - reportData:', reportData);
    
    if (!reportData) {
      toast.warning('No report data available. Please generate a report first.');
      return;
    }
    
    if (!reportData.records || reportData.records.length === 0) {
      toast.warning('No attendance records to generate PDF');
      return;
    }

    console.log('✅ Starting PDF generation with', reportData.records.length, 'records');

    try {
      console.log('✅ jsPDF libraries loaded');
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      console.log('✅ PDF document created');
      console.log('✅ autoTable available:', typeof doc.autoTable);
      
      // Title
      doc.setFontSize(16);
      doc.text('Attendance Report', pageWidth / 2, 15, { align: 'center' });
      
      // Report details
      doc.setFontSize(10);
      let yPos = 25;
      doc.text('Report Type: ' + (reportType.charAt(0).toUpperCase() + reportType.slice(1)), 14, yPos);
      yPos += 6;
      
      if (reportType === 'daily') {
        doc.text('Date: ' + selectedDate, 14, yPos);
      } else if (reportType === 'monthly') {
        doc.text('Month: ' + selectedMonth, 14, yPos);
      } else {
        doc.text('Period: ' + startDate + ' to ' + endDate, 14, yPos);
      }
      yPos += 6;
      
      if (reportData.employeeName && reportData.employeeName !== 'All Employees') {
        doc.text('Employee: ' + reportData.employeeName, 14, yPos);
        yPos += 6;
      }
      
      if (selectedDepartment) {
        const dept = departments.find(d => d.id === parseInt(selectedDepartment));
        if (dept && dept.name) {
          doc.text('Department: ' + dept.name, 14, yPos);
          yPos += 6;
        }
      }
      
      if (selectedRole) {
        const role = roles.find(r => r.id === parseInt(selectedRole));
        if (role && role.role_name) {
          doc.text('Role: ' + role.role_name, 14, yPos);
          yPos += 6;
        }
      }
      
      doc.text('Total Records: ' + reportData.count, 14, yPos);
      yPos += 6;
      
      console.log('✅ PDF header created, preparing table data...');
      
      // Table data - format dates and times properly
      const tableData = reportData.records.map((record, index) => {
        // Format date - convert to local date to avoid timezone issues
        let dateStr = record.date || '-';
        if (dateStr !== '-') {
          try {
            const dateObj = new Date(dateStr);
            // Format as YYYY-MM-DD in local timezone
            dateStr = dateObj.toLocaleDateString('en-CA'); // en-CA gives YYYY-MM-DD format
          } catch (e) {
            // Fallback: just remove time if present
            if (dateStr.includes('T')) {
              dateStr = dateStr.split('T')[0];
            }
          }
        }
        
        // Format check in time (extract time only)
        let checkIn = record.check_in || record.first_in || '-';
        if (checkIn !== '-') {
          if (checkIn.includes('T')) {
            checkIn = checkIn.split('T')[1]?.split('.')[0] || checkIn;
          }
        }
        
        // Format check out time (extract time only)
        let checkOut = record.check_out || record.last_out || '-';
        if (checkOut !== '-') {
          if (checkOut.includes('T')) {
            checkOut = checkOut.split('T')[1]?.split('.')[0] || checkOut;
          }
        }

        // Add location if available (mobile records)
        let location = '-';
        if (record.checkin_latitude && record.checkin_longitude) {
          location = `${Number(record.checkin_latitude).toFixed(4)}, ${Number(record.checkin_longitude).toFixed(4)}`;
        }
        
        return [
          (index + 1).toString(),
          (record.person_name || record.name || '-').toString(),
          (record.employee_id || '-').toString(),
          dateStr,
          checkIn,
          checkOut,
          (record.status || '-').toString(),
          location
        ];
      });
      
      console.log('✅ Table data prepared:', tableData.length, 'rows');
      
      autoTable(doc, {
        startY: yPos + 5,
        head: [['#', 'Name', 'Emp ID', 'Date', 'Check In', 'Check Out', 'Status', 'Location']],
        body: tableData,
        theme: 'grid',
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [123, 115, 255] },
        columnStyles: {
          7: { cellWidth: 30 } // Location column wider
        }
      });
      
      console.log('✅ Table added to PDF, saving...');
      
      // Save
      const fileName = 'attendance_' + reportType + '_' + Date.now() + '.pdf';
      doc.save(fileName);
      
      console.log('✅ PDF saved:', fileName);
    } catch (error) {
      console.error('❌ PDF generation error:', error);
      console.error('Error stack:', error.stack);
      toast.error('Failed to generate PDF: ' + error.message);
    }
  };

  const handleDownloadExcel = async () => {
    console.log('🔍 Excel Download - reportData:', reportData);
    
    if (!reportData) {
      toast.warning('No report data available. Please generate a report first.');
      return;
    }
    
    if (!reportData.records || reportData.records.length === 0) {
      toast.warning('No attendance records to generate Excel');
      return;
    }

    console.log('✅ Starting Excel generation with', reportData.records.length, 'records');

    try {
      // Prepare data for Excel
      const excelData = reportData.records.map((record, index) => {
        // Format date - convert to local date to avoid timezone issues
        let dateStr = record.date || '-';
        if (dateStr !== '-') {
          try {
            const dateObj = new Date(dateStr);
            // Format as YYYY-MM-DD in local timezone
            dateStr = dateObj.toLocaleDateString('en-CA'); // en-CA gives YYYY-MM-DD format
          } catch (e) {
            // Fallback: just remove time if present
            if (dateStr.includes('T')) {
              dateStr = dateStr.split('T')[0];
            }
          }
        }

        // Format check in time
        let checkIn = record.check_in || record.first_in || '-';
        if (checkIn !== '-') {
          if (checkIn.includes('T')) {
            checkIn = checkIn.split('T')[1]?.split('.')[0] || checkIn;
          }
        }

        // Format check out time
        let checkOut = record.check_out || record.last_out || '-';
        if (checkOut !== '-') {
          if (checkOut.includes('T')) {
            checkOut = checkOut.split('T')[1]?.split('.')[0] || checkOut;
          }
        }

        // Add location if available (mobile records)
        let location = '-';
        if (record.checkin_latitude && record.checkin_longitude) {
          location = `${Number(record.checkin_latitude).toFixed(6)}, ${Number(record.checkin_longitude).toFixed(6)}`;
        }

        return {
          '#': index + 1,
          'Employee ID': record.employee_id || '-',
          'Employee Name': record.person_name || record.name || '-',
          'Department': record.department || '-',
          'Role': record.role || '-',
          'Date': dateStr,
          'Check In': checkIn,
          'Check Out': checkOut,
          'Status': record.status || '-',
          'Late Status': record.late_status || 'On-Time',
          'Email': record.email || '-',
          'Location (Lat, Long)': location
        };
      });

      // Create a new workbook
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance Report');

      // Add styles (basic)
      worksheet['!cols'] = [
        { wch: 5 },   // #
        { wch: 20 },  // Employee Name
        { wch: 15 },  // Department
        { wch: 15 },  // Role
        { wch: 12 },  // Date
        { wch: 12 },  // Check In
        { wch: 12 },  // Check Out
        { wch: 12 },  // Status
        { wch: 15 },  // Late Status
        { wch: 20 }   // Email
      ];

      // Generate filename
      const fileName = `attendance_report_${reportType}_${Date.now()}.xlsx`;

      // Save the file
      XLSX.writeFile(workbook, fileName);

      console.log('✅ Excel file saved:', fileName);
    } catch (error) {
      console.error('❌ Excel generation error:', error);
      toast.error('Failed to generate Excel report: ' + error.message);
    }
  };

  const handleClearFilters = () => {
    setReportType('daily');
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setSelectedMonth(new Date().toISOString().slice(0, 7));
    setStartDate(new Date().toISOString().split('T')[0]);
    setEndDate(new Date().toISOString().split('T')[0]);
    setSelectedDepartment('');
    setSelectedRole('');
    setSelectedEmployee('');
    setSelectedStatus(''); // Clear status filter
    setReportData(null);
    toast.info('Filters cleared');
  };

  const filteredEmployees = employees.filter(emp => {
    // Filter by department (handle both department_id FK and department VARCHAR)
    if (selectedDepartment) {
      const deptMatches = emp.department_id === parseInt(selectedDepartment) || 
                         emp.department === selectedDepartment ||
                         emp.departmentname === selectedDepartment;
      if (!deptMatches) return false;
    }
    
    // Filter by role (handle both role_id FK and role VARCHAR)
    if (selectedRole) {
      const roleMatches = emp.role_id === parseInt(selectedRole) || 
                         emp.role === selectedRole ||
                         emp.rolename === selectedRole;
      if (!roleMatches) return false;
    }
    
    return true;
  });

  return (
    <div className="attendance-report">
      <div className="page-header">
        <h2 className="attendance-report-title">
          <FileText size={24} className="attendance-report-title-icon" />
          <span>ATTENDANCE REPORT</span>
        </h2>
      </div>

      <div className="report-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Select Report Type:</label>
            <select 
              value={reportType} 
              onChange={(e) => setReportType(e.target.value)}
              className="filter-select"
            >
              <option value="daily">Daily Report</option>
              <option value="monthly">Monthly Report</option>
              <option value="custom">Custom Date Range</option>
            </select>
          </div>

          {reportType === 'custom' ? (
            <>
              <div className="filter-group">
                <label>Start Date:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="filter-input"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="filter-group">
                <label>End Date:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="filter-input"
                  min={startDate}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </>
          ) : (
            <>
              <div className="filter-group">
                <label>Attendance Status:</label>
                <select 
                  value={selectedStatus} 
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Statuses</option>
                  <option value="Present">Present</option>
                  <option value="Absent">Absent</option>
                  <option value="Leave">Leave</option>
                  <option value="Half">Half-day</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Select Date:</label>
                {reportType === 'daily' ? (
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="filter-input"
                    max={new Date().toISOString().split('T')[0]}
                  />
                ) : (
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="filter-input"
                    max={new Date().toISOString().slice(0, 7)}
                  />
                )}
              </div>
            </>
          )}
        </div>

        <div className="filter-row filter-row-secondary">
          <div className="filter-group">
            <label>Department:</label>
            <select 
              value={selectedDepartment} 
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="filter-select"
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.departmentname}>{dept.departmentname}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Role:</label>
            <select 
              value={selectedRole} 
              onChange={(e) => setSelectedRole(e.target.value)}
              className="filter-select"
            >
              <option value="">All Roles</option>
              {roles.map(role => (
                <option key={role.id} value={role.rolename}>{role.rolename}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Select Contact:</label>
            <select 
              value={selectedEmployee} 
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="filter-select"
            >
              <option value="">All Employees</option>
              {filteredEmployees.map(emp => {
                const employeeName = emp.person_name || emp.name || 'Unknown';
                const employeeId = emp.employee_id || emp.display_id;
                return (
                  <option key={employeeId} value={employeeId}>
                    {employeeName} (ID: {employeeId})
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        <div className="action-buttons">
          
          <button className="btn-show-report" onClick={handleShowReport}>
            <Calendar size={18} />
            Show Report
          </button>
          <button className="btn-clear" onClick={handleClearFilters}>
            <X size={18} />
            Clear
          </button>
        </div>
      </div>

      {loading && (
        <div className="loading-spinner">Loading report...</div>
      )}

      {reportData && !loading && (
        <div className="report-display">
          <div className="report-header-info" style={{
            background: '#0b3c5d',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            marginBottom: '10px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: isMobileView ? 'stretch' : 'center',
              flexDirection: isMobileView ? 'column' : 'row',
              gap: isMobileView ? '8px' : '0',
              marginBottom: '6px'
            }}>
              <h3 style={{
                margin: '0',
                marginLeft: isMobileView ? '0' : '5px',
                fontSize: isMobileView ? '14px' : '16px',
                textAlign: isMobileView ? 'left' : 'inherit'
              }}>📊 ATTENDANCE SUMMARY REPORT</h3>
              <div style={{ position: 'relative' }}>
                <button 
                  className="btn-download" 
                  onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    padding: isMobileView ? '8px 10px' : '5px 10px',
                    background: 'rgba(255, 255, 255, 0.15)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '4px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '11px',
                    transition: 'all 0.2s',
                    width: isMobileView ? '100%' : 'auto'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  }}
                >
                  <Download size={16} />
                  Download Report
                  <span style={{ marginLeft: '2px' }}>▼</span>
                </button>

                {showDownloadMenu && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: isMobileView ? 'auto' : 0,
                    left: isMobileView ? 0 : 'auto',
                    marginTop: '4px',
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                    zIndex: 1000,
                    minWidth: isMobileView ? '180px' : '160px',
                    overflow: 'hidden'
                  }}>
                    <button
                      onClick={() => {
                        handleDownloadPDF();
                        setShowDownloadMenu(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '7px 10px',
                        border: 'none',
                        background: 'white',
                        color: '#1e293b',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11px',
                        fontWeight: '500',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f1f5f9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      <span style={{ color: '#667eea', fontSize: '13px' }}>📄</span>
                      Download as PDF
                    </button>
                    <div style={{ height: '1px', background: '#e2e8f0' }}></div>
                    <button
                      onClick={() => {
                        handleDownloadExcel();
                        setShowDownloadMenu(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '7px 10px',
                        border: 'none',
                        background: 'white',
                        color: '#1e293b',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11px',
                        fontWeight: '500',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f1f5f9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      <span style={{ color: '#10b981', fontSize: '15px' }}>📊</span>
                      Download as Excel
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="report-meta" style={{ 
              background: '#0b3c5d',
              display: 'grid', 
              gridTemplateColumns: isMobileView ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: '6px',
              padding: '8px 12px',
            }}>
              {reportData.employeeName && (
                <span>Employee: <strong>{reportData.employeeName}</strong></span>
              )}
              <span>Report Date: <strong>{reportType === 'daily' ? new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : reportType}</strong></span>
              <span>Total Records: <strong>{reportData.count || 0}</strong></span>
              <span>
                Status: <strong style={{
                  background: '0b3c5d22',
                  padding: isMobileView ? '3px 8px' : '4px 12px',
                  borderRadius: '12px',
                  marginLeft: '4px',
                }}>
                  {reportData.count > 0 ? 'Data Available' : 'No Data'}
                </strong>
              </span>
            </div>
          </div>

          <div className="daily-report-table" style={{ 
            background: 'white',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {(() => {
              // Calculate filtered records
              const filteredRecords = reportData.records && reportData.records.length > 0 
                ? reportData.records.filter(record => !selectedStatus || record.status === selectedStatus)
                : [];
              
              // Calculate pagination
              const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
              const startIndex = (currentPage - 1) * itemsPerPage;
              const endIndex = startIndex + itemsPerPage;
              const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

              // Pagination UI helpers
              const getPageNumbers = () => {
                const maxVisiblePages = isMobileView ? 5 : 10;
                const pageOffset = Math.floor(maxVisiblePages / 2);
                let startPage = Math.max(1, currentPage - pageOffset);
                let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                if (endPage - startPage < maxVisiblePages - 1) {
                  startPage = Math.max(1, endPage - (maxVisiblePages - 1));
                }
                const pages = [];
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(i);
                }
                return pages;
              };

              const getCompactPageItems = () => {
                if (!isMobileView || totalPages <= 5) {
                  return getPageNumbers();
                }

                const items = [1];
                const middleStart = Math.max(2, currentPage - 1);
                const middleEnd = Math.min(totalPages - 1, currentPage + 1);

                if (middleStart > 2) {
                  items.push('ellipsis-left');
                }

                for (let page = middleStart; page <= middleEnd; page += 1) {
                  items.push(page);
                }

                if (middleEnd < totalPages - 1) {
                  items.push('ellipsis-right');
                }

                items.push(totalPages);
                return items;
              };

              return (
                <>
                  <div className="attendance-report-table-wrap" style={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    maxHeight: isMobileView ? '420px' : '450px'
                  }}>
                    <table className="attendance-report-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobileView ? '760px' : '900px' }}>
                      <thead style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 10 }}>
                        <tr>
                          <th style={{ padding: '8px', textAlign: 'left', fontWeight: '800', color: '#475569', fontSize: '12px' }}>Employee</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontWeight: '800', color: '#475569', fontSize: '12px' }}>Department</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontWeight: '800', color: '#475569', fontSize: '12px' }}>Role</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontWeight: '800', color: '#475569', fontSize: '12px' }}>Date</th>
                          <th style={{ padding: '8px', textAlign: 'center', fontWeight: '800', color: '#475569', fontSize: '12px' }}>Status</th>
                          <th style={{ padding: '8px', textAlign: 'center', fontWeight: '800', color: '#475569', fontSize: '12px' }}>Check In</th>
                          <th style={{ padding: '8px', textAlign: 'center', fontWeight: '800', color: '#475569', fontSize: '12px' }}>Check Out</th>
                          <th style={{ padding: '8px', textAlign: 'center', fontWeight: '800', color: '#475569', fontSize: '12px' }}>Late Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRecords.length > 0 ? (
                          paginatedRecords.map((record, index) => (
                          <tr key={index} style={{ 
                            borderBottom: '1px solid #e2e8f0',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                          >
                            <td style={{ padding: '8px' }} data-label="Employee">
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '12px' }}>{record.name || 'N/A'}</div>
                                  <div style={{ fontSize: '10px', color: '#64748b' }}>{record.email || 'No email'}</div>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '8px', color: '#475569', fontSize: '11.5px' }} data-label="Department">
                              <div>{record.department || 'N/A'}</div>
                            </td>
                            <td style={{ padding: '8px', color: '#94a3b8' }} data-label="Role">
                              <div style={{ fontSize: '11px' }}>{record.role || 'N/A'}</div>
                            </td>
                            <td style={{ padding: '8px', color: '#475569' }} data-label="Date">
                              <div style={{ fontWeight: '500', fontSize: '11.5px' }}>
                                {record.date ? new Date(record.date).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: 'numeric'
                                }) : 'N/A'}
                              </div>
                              {record.checkin_latitude && record.checkin_longitude && (
                                <div style={{ fontSize: '9.5px', color: '#94a3b8', marginTop: '1px' }}>
                                  📍 {Number(record.checkin_latitude).toFixed(4)}, {Number(record.checkin_longitude).toFixed(4)}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }} data-label="Status">
                              {(() => {
                                const status = (record.status || '').toLowerCase();
                                let bgColor, textColor, icon;
                                
                                if (status === 'present' || status === 'p') {
                                  bgColor = '#dcfce7';
                                  textColor = '#166534';
                                  icon = <CheckCircle size={11} />;
                                } else if (status === 'absent' || status === 'a') {
                                  bgColor = '#fee2e2';
                                  textColor = '#991b1b';
                                  icon = <XCircle size={11} />;
                                } else if (status === 'leave' || status === 'l') {
                                  bgColor = '#dbeafe';
                                  textColor = '#1e40af';
                                  icon = <Calendar size={11} />;
                                } else if (status === 'half-day' || status === 'half' || status === 'h') {
                                  bgColor = '#fef3c7';
                                  textColor = '#92400e';
                                  icon = <Clock size={11} />;
                                } else if (status === 'pending') {
                                  bgColor = '#e0e7ff';
                                  textColor = '#3730a3';
                                  icon = <AlertCircle size={11} />;
                                } else {
                                  bgColor = '#f1f5f9';
                                  textColor = '#64748b';
                                  icon = <AlertCircle size={11} />;
                                }
                                
                                return (
                                  <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    padding: '3px 8px',
                                    borderRadius: '9px',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    background: bgColor,
                                    color: textColor
                                  }}>
                                    {icon}
                                    {record.status || 'N/A'}
                                  </span>
                                );
                              })()}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }} data-label="Check In">
                              <div style={{ 
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                color: '#475569',
                                fontWeight: '500',
                                fontSize: '11.5px'
                              }}>
                                <Clock size={11} color="#64748b" />
                                {record.first_in || 'N/A'}
                              </div>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }} data-label="Check Out">
                              <div style={{ 
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                color: '#475569',
                                fontWeight: '500',
                                fontSize: '11.5px'
                              }}>
                                <Clock size={11} color="#64748b" />
                                {record.last_out || 'N/A'}
                              </div>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }} data-label="Late Status">
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                padding: '3px 8px',
                                borderRadius: '9px',
                                fontSize: '11px',
                                fontWeight: '500',
                                background: record.late_status === 'Late' ? '#fef3c7' : '#dbeafe',
                                color: record.late_status === 'Late' ? '#92400e' : '#1e40af'
                              }}>
                                {record.late_status === 'Late' ? <AlertCircle size={11} /> : <CheckCircle size={11} />}
                                {record.late_status || 'On-Time'}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" style={{ 
                            padding: '20px',
                            textAlign: 'center',
                            color: '#94a3b8'
                          }}>
                            <FileText size={32} color="#cbd5e1" style={{ marginBottom: '8px' }} />
                            <div style={{ fontSize: '13px', fontWeight: '500', color: '#64748b' }}>No attendance records found</div>
                            <div style={{ fontSize: '10px', marginTop: '4px' }}>Try adjusting your filters or date range</div>
                          </td>
                        </tr>
                      )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  {filteredRecords.length > 0 && (
                    <div style={{
                      borderTop: '1px solid #e2e8f0',
                      padding: isMobileView ? '4px 6px 3px' : '10px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: isMobileView ? 'space-between' : 'center',
                      gap: isMobileView ? '4px' : '4px',
                      flexWrap: 'wrap',
                      background: '#f8fafc',
                      position: isMobileView ? 'sticky' : 'static',
                      bottom: isMobileView ? 0 : 'auto',
                      zIndex: isMobileView ? 20 : 'auto'
                    }}>
                      {/* Previous Button */}
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        style={{
                          padding: isMobileView ? '2px 6px' : '6px 10px',
                          borderRadius: isMobileView ? '5px' : '6px',
                          border: '1px solid #e2e8f0',
                          background: currentPage === 1 ? '#f1f5f9' : 'white',
                          color: currentPage === 1 ? '#cbd5e1' : '#0b3c5d',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          fontSize: isMobileView ? '8px' : '10px',
                          fontWeight: '600',
                          transition: 'all 0.2s',
                          opacity: currentPage === 1 ? 0.5 : 1,
                          minWidth: isMobileView ? '26px' : 'auto',
                          height: isMobileView ? '24px' : 'auto'
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage !== 1) {
                            e.currentTarget.style.background = '#0b3c5d';
                            e.currentTarget.style.color = 'white';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = currentPage === 1 ? '#f1f5f9' : 'white';
                          e.currentTarget.style.color = currentPage === 1 ? '#cbd5e1' : '#0b3c5d';
                        }}
                      >
                        {isMobileView ? '←' : '← Prev'}
                      </button>

                      {/* Page Numbers */}
                      <div style={{
                        display: 'flex',
                        gap: '2px',
                        alignItems: 'center',
                        maxWidth: isMobileView ? '140px' : 'none',
                        overflowX: isMobileView ? 'auto' : 'visible'
                      }}>
                        {getCompactPageItems().map((pageItem, index) => {
                          if (typeof pageItem !== 'number') {
                            return (
                              <span
                                key={`${pageItem}-${index}`}
                                style={{
                                  width: isMobileView ? '16px' : '20px',
                                  textAlign: 'center',
                                  fontSize: isMobileView ? '9px' : '10px',
                                  color: '#64748b',
                                  fontWeight: 600
                                }}
                              >
                                ...
                              </span>
                            );
                          }

                          return (
                            <button
                              key={pageItem}
                              onClick={() => setCurrentPage(pageItem)}
                              style={{
                                width: isMobileView ? '24px' : '28px',
                                height: isMobileView ? '24px' : '28px',
                                borderRadius: isMobileView ? '5px' : '6px',
                                border: '1px solid #e2e8f0',
                                background: currentPage === pageItem ? '#0b3c5d' : 'white',
                                color: currentPage === pageItem ? 'white' : '#475569',
                                cursor: 'pointer',
                                fontSize: isMobileView ? '8px' : '10px',
                                fontWeight: currentPage === pageItem ? '700' : '600',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: '0 0 auto'
                              }}
                              onMouseEnter={(e) => {
                                if (currentPage !== pageItem) {
                                  e.currentTarget.style.background = '#f1f5f9';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = currentPage === pageItem ? '#0b3c5d' : 'white';
                              }}
                            >
                              {pageItem}
                            </button>
                          );
                        })}
                      </div>

                      {/* Next Button */}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        style={{
                          padding: isMobileView ? '2px 6px' : '6px 10px',
                          borderRadius: isMobileView ? '5px' : '6px',
                          border: '1px solid #e2e8f0',
                          background: currentPage === totalPages ? '#f1f5f9' : 'white',
                          color: currentPage === totalPages ? '#cbd5e1' : '#0b3c5d',
                          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                          fontSize: isMobileView ? '8px' : '10px',
                          fontWeight: '600',
                          transition: 'all 0.2s',
                          opacity: currentPage === totalPages ? 0.5 : 1,
                          minWidth: isMobileView ? '26px' : 'auto',
                          height: isMobileView ? '24px' : 'auto'
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage !== totalPages) {
                            e.currentTarget.style.background = '#0b3c5d';
                            e.currentTarget.style.color = 'white';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = currentPage === totalPages ? '#f1f5f9' : 'white';
                          e.currentTarget.style.color = currentPage === totalPages ? '#cbd5e1' : '#0b3c5d';
                        }}
                      >
                        {isMobileView ? '→' : 'Next →'}
                      </button>

                      {/* Records Info */}
                      <div style={{
                        fontSize: isMobileView ? '8px' : '10px',
                        color: '#64748b',
                        marginLeft: isMobileView ? '0' : '8px',
                        fontWeight: '500',
                        width: isMobileView ? '100%' : 'auto',
                        textAlign: isMobileView ? 'center' : 'left'
                      }}>
                        Showing {startIndex + 1}-{Math.min(endIndex, filteredRecords.length)} of {filteredRecords.length}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceReport;
